import { API, expressAPI } from 'src/utils/interceptor'

const wastageBase = venueId => `venue/${venueId}/wastage`

const requireVenueId = venueId => {
  if (!venueId) throw new Error('venueId is required')
}

const requireEntryId = entryId => {
  if (!entryId) throw new Error('entryId is required')
}

/**
 * List filters Express already understands. Empty values are left off the query.
 * @param {{ from?: string, to?: string, product?: string, recipe?: string, user?: string, reason?: string, q?: string, includeRemoved?: boolean }} [filters]
 */
export const wastageListQuery = (filters = {}) => {
  const query = {}
  if (filters.from) query.from = filters.from
  if (filters.to) query.to = filters.to
  if (filters.product) query.product = filters.product
  if (filters.recipe) query.recipe = filters.recipe
  if (filters.user) query.user = filters.user
  if (filters.reason) query.reason = filters.reason
  if (filters.q) query.q = filters.q
  if (filters.includeRemoved) query.includeRemoved = 'true'
  return Object.keys(query).length ? query : undefined
}

const filenameFromContentDisposition = headerValue => {
  if (!headerValue) return ''
  const utf8Match = String(headerValue).match(/filename\*=UTF-8''([^;]+)/i)
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]).replace(/["']/g, '')
  }
  const fallbackMatch = String(headerValue).match(/filename="?([^";]+)"?/i)
  return fallbackMatch?.[1] || ''
}

const readErrorMessage = async data => {
  if (!data) return ''
  if (typeof data === 'string') return data
  if (typeof data.message === 'string' && data.message) return data.message
  if (typeof data.text === 'function') {
    try {
      const text = await data.text()
      const parsed = JSON.parse(text)
      return parsed?.message || ''
    } catch {
      return ''
    }
  }
  return ''
}

/**
 * Express error JSON becomes an Error screens can show. The status stays on the error.
 * @param {unknown} error
 * @param {string} fallback
 */
export async function toWastageApiError(error, fallback) {
  if (error?.statusCode && !error?.response && error instanceof Error) {
    return error
  }
  const message =
    (await readErrorMessage(error?.response?.data)) ||
    error?.message ||
    fallback
  const err = new Error(message || fallback)
  err.statusCode = error?.response?.status || error?.statusCode
  return err
}

const unwrap = async (request, fallback) => {
  try {
    const response = await request()
    if (!response?.success) {
      const err = new Error(response?.message || fallback)
      err.statusCode = 400
      throw err
    }
    return response.data
  } catch (error) {
    throw await toWastageApiError(error, fallback)
  }
}

/**
 * Current filter: entries, count, and totals.
 * @param {string} venueId
 * @param {{ from?: string, to?: string, product?: string, user?: string, reason?: string, q?: string, apiGet?: Function }} [options]
 */
export async function listWastageEntries(venueId, options = {}) {
  requireVenueId(venueId)
  const { apiGet = API.get, ...filters } = options
  const query = wastageListQuery(filters)
  return unwrap(
    () =>
      apiGet('api2', wastageBase(venueId), {
        ...(query ? { queryStringParameters: query } : {}),
      }),
    'Waste could not be loaded',
  )
}

/**
 * One entry, including its audit.
 * @param {string} venueId
 * @param {string} entryId
 * @param {{ apiGet?: Function }} [options]
 */
export async function getWastageEntry(
  venueId,
  entryId,
  { apiGet = API.get } = {},
) {
  requireVenueId(venueId)
  requireEntryId(entryId)
  return unwrap(
    () => apiGet('api2', `${wastageBase(venueId)}/${entryId}`),
    'That waste record could not be loaded',
  )
}

/**
 * Create a product or recipe entry. The body is the Express payload, including kind.
 * @param {string} venueId
 * @param {object} body
 * @param {{ apiPost?: Function }} [options]
 */
export async function createWastageEntry(
  venueId,
  body,
  { apiPost = API.post } = {},
) {
  requireVenueId(venueId)
  return unwrap(
    () => apiPost('api2', wastageBase(venueId), { body: body || {} }),
    'Waste could not be saved',
  )
}

/**
 * Correct an entry. Who recorded it first stays on the server.
 * @param {string} venueId
 * @param {string} entryId
 * @param {object} body
 * @param {{ apiPatch?: Function }} [options]
 */
export async function editWastageEntry(
  venueId,
  entryId,
  body,
  { apiPatch = API.patch } = {},
) {
  requireVenueId(venueId)
  requireEntryId(entryId)
  return unwrap(
    () =>
      apiPatch('api2', `${wastageBase(venueId)}/${entryId}`, {
        body: body || {},
      }),
    'Waste could not be updated',
  )
}

/**
 * Soft-delete an entry.
 * @param {string} venueId
 * @param {string} entryId
 * @param {{ apiDel?: Function }} [options]
 */
export async function deleteWastageEntry(
  venueId,
  entryId,
  { apiDel = API.del } = {},
) {
  requireVenueId(venueId)
  requireEntryId(entryId)
  return unwrap(
    () => apiDel('api2', `${wastageBase(venueId)}/${entryId}`),
    'Waste could not be removed',
  )
}

const fallbackCsvName = filters => {
  const from = String(filters.from || '').trim()
  const to = String(filters.to || '').trim()
  if (/^\d{4}-\d{2}-\d{2}$/.test(from) && /^\d{4}-\d{2}-\d{2}$/.test(to)) {
    return `waste-${from}-to-${to}.csv`
  }
  return 'waste.csv'
}

/**
 * CSV of the current filter. Returns the file and the name Express sent.
 * @param {string} venueId
 * @param {{ from?: string, to?: string, product?: string, user?: string, reason?: string, q?: string, httpGet?: Function }} [options]
 */
export async function downloadWastageCsv(venueId, options = {}) {
  requireVenueId(venueId)
  const { httpGet = expressAPI.get, ...filters } = options
  const query = wastageListQuery(filters)
  try {
    const response = await httpGet(`${wastageBase(venueId)}/export`, {
      ...(query ? { params: query } : {}),
      responseType: 'blob',
    })
    const filename =
      filenameFromContentDisposition(
        response?.headers?.['content-disposition'],
      ) || fallbackCsvName(filters)
    const blob =
      response?.data instanceof Blob
        ? response.data
        : new Blob([response?.data ?? ''], { type: 'text/csv;charset=utf-8' })
    return { blob, filename, contentType: 'text/csv' }
  } catch (error) {
    throw await toWastageApiError(error, 'Waste could not be downloaded')
  }
}

/** Starts a browser download of the CSV Express returned. */
export function saveWastageCsvFile({ blob, filename }) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename || 'waste.csv'
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}
